import { Controller } from "@hotwired/stimulus"

// Validates a phone number against per-country rules, shows a live hint
// and blocks form submission if the value is invalid.
//
// Usage:
//   <form data-controller="phone-input" data-action="submit->phone-input#onSubmit">
//     <select data-phone-input-target="country" data-action="change->phone-input#onCountryChange">...</select>
//     <input  data-phone-input-target="phone" data-action="input->phone-input#onInput blur->phone-input#onBlur" />
//     <p data-phone-input-target="hint"></p>
//     <p data-phone-input-target="error" class="hidden"></p>
//   </form>
export default class extends Controller {
  static targets = ["country", "phone", "hint", "error"]

  static PHONE_RULES = {
    "+971": { country: "UAE",          length: [9],    startsWith: ["5"],               example: "501234567" },
    "+966": { country: "Saudi Arabia", length: [9],    startsWith: ["5"],               example: "501234567" },
    "+961": { country: "Lebanon",      length: [7, 8], startsWith: null,                example: "71234567"  },
    "+974": { country: "Qatar",        length: [8],    startsWith: ["3","5","6","7"],   example: "31234567"  },
    "+965": { country: "Kuwait",       length: [8],    startsWith: ["5","6","9"],       example: "51234567"  },
    "+973": { country: "Bahrain",      length: [8],    startsWith: ["3","6","8","9"],   example: "36123456"  },
    "+968": { country: "Oman",         length: [8],    startsWith: ["7","9"],           example: "71234567"  }
  }

  connect() {
    this.updateHint()
  }

  onCountryChange() {
    this.updateHint()
    if (this.hasPhoneTarget && this.phoneTarget.value) {
      this.validate()
    }
  }

  onInput() {
    // Strip non-digits as the user types (keeps the field clean)
    const cleaned = this.phoneTarget.value.replace(/[^\d]/g, "")
    if (cleaned !== this.phoneTarget.value) {
      this.phoneTarget.value = cleaned
    }
    this.clearError()
  }

  onBlur() {
    if (this.phoneTarget.value) this.validate()
  }

  onSubmit(event) {
    if (!this.validate()) {
      event.preventDefault()
      event.stopPropagation()
      this.phoneTarget.focus()
    }
  }

  // --- helpers ---

  currentRule() {
    const code = this.hasCountryTarget ? this.countryTarget.value : null
    return this.constructor.PHONE_RULES[code]
  }

  updateHint() {
    if (!this.hasHintTarget) return
    const rule = this.currentRule()
    this.hintTarget.textContent = rule
      ? `Enter a ${rule.length.join(" or ")}-digit ${rule.country} number (e.g. ${rule.example}).`
      : ""
  }

  validate() {
    const rule = this.currentRule()
    const raw = (this.phoneTarget.value || "").replace(/\D/g, "").replace(/^0+/, "")

    if (!raw) {
      this.showError("Please enter your phone number.")
      return false
    }

    if (!rule) {
      this.clearError()
      return true
    }

    if (!rule.length.includes(raw.length)) {
      this.showError(
        `Phone number must be ${rule.length.join(" or ")} digits for ${rule.country} (e.g. ${rule.example}).`
      )
      return false
    }

    if (rule.startsWith && !rule.startsWith.includes(raw[0])) {
      this.showError(
        `For ${rule.country}, the number must start with ${rule.startsWith.join(", ")} (e.g. ${rule.example}).`
      )
      return false
    }

    this.clearError()
    return true
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove("hidden")
    }
    this.phoneTarget.classList.add("border-red-300")
    this.phoneTarget.classList.remove("border-slate-300")
    this.phoneTarget.setAttribute("aria-invalid", "true")
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = ""
      this.errorTarget.classList.add("hidden")
    }
    this.phoneTarget.classList.remove("border-red-300")
    this.phoneTarget.classList.add("border-slate-300")
    this.phoneTarget.removeAttribute("aria-invalid")
  }
}
